import { collectionGroup, limit, query, where } from 'firebase/firestore'
import { useMemo } from 'react'
import { Enrollment } from '../../../pure-js/types/GrooverTypes'
import { db } from '../libs/Firebase'
import { getDisplayNameFromEnrollment } from '../libs/PortalDisplayNameHelper'
import { useFirestoreQuery } from './useFirestoreQuery'

/**
 * Lazy-load portal display name from the user's first matching enrollment doc.
 * Uses limit(1) collection-group queries — not the full enrollments list hook.
 */
export const usePortalDisplayName = (enrollmentUserId: string | null) => {
  const qMain = useMemo(() => {
    if (!enrollmentUserId) {
      return query(collectionGroup(db, 'dummy-enrollments'))
    }
    return query(collectionGroup(db, 'enrollments'), where('mainUser.id', '==', enrollmentUserId), limit(1))
  }, [enrollmentUserId])

  const qPartner = useMemo(() => {
    if (!enrollmentUserId) {
      return query(collectionGroup(db, 'dummy-enrollments'))
    }
    return query(collectionGroup(db, 'enrollments'), where('partnerUser.id', '==', enrollmentUserId), limit(1))
  }, [enrollmentUserId])

  const main = useFirestoreQuery<Enrollment[]>(qMain, {
    fnName: 'usePortalDisplayName-main',
    enabled: !!enrollmentUserId
  })

  const partner = useFirestoreQuery<Enrollment[]>(qPartner, {
    fnName: 'usePortalDisplayName-partner',
    enabled: !!enrollmentUserId && !main.data?.length && !main.isLoading
  })

  const displayName = useMemo(() => {
    if (!enrollmentUserId) {
      return undefined
    }
    const mainEnrollment = main.data?.[0]
    if (mainEnrollment) {
      return getDisplayNameFromEnrollment(enrollmentUserId, mainEnrollment)
    }
    const partnerEnrollment = partner.data?.[0]
    if (partnerEnrollment) {
      return getDisplayNameFromEnrollment(enrollmentUserId, partnerEnrollment)
    }
    return undefined
  }, [enrollmentUserId, main.data, partner.data])

  const isLoading = !!enrollmentUserId && (main.isLoading || (!main.data?.length && !main.error && partner.isLoading))

  return { displayName, isLoading }
}
